import type { EtapaProceso } from "../../shared/types";

// Funciones puras para el Diseñador Visual de Procesos: derivan todo de
// datos ya fetcheados (GET /procesos/:procesoId/etapas y
// GET /procesos/:id/kanban, ambos ya existentes) — cero lógica nueva del
// lado del servidor, cero fetch dentro de este módulo.

export interface ExpedienteKanban {
  id: string;
  dias_en_etapa: number | null;
  dias_en_proceso: number | null;
  etapa_vencida: boolean;
}

export interface ColumnaKanban {
  etapa: EtapaProceso;
  expedientes: ExpedienteKanban[];
}

export interface ResumenProceso {
  cantidadEtapas: number;
  cantidadExpedientesActivos: number;
  tiempoPromedioTotalDias: number | null;
  tiempoObjetivoTotalDias: number | null;
}

function promedio(valores: number[]): number | null {
  if (valores.length === 0) return null;
  return Math.round((valores.reduce((acc, v) => acc + v, 0) / valores.length) * 10) / 10;
}

export function calcularResumenProceso(etapas: EtapaProceso[], columnas: ColumnaKanban[]): ResumenProceso {
  const etapasActivas = etapas.filter((e) => e.activo);
  const todosLosExpedientes = columnas.flatMap((c) => c.expedientes);
  const diasEnProceso = todosLosExpedientes
    .map((e) => e.dias_en_proceso)
    .filter((d): d is number => d != null);
  const tiemposObjetivo = etapasActivas
    .map((e) => e.tiempo_objetivo_dias)
    .filter((d): d is number => d != null);
  return {
    cantidadEtapas: etapasActivas.length,
    cantidadExpedientesActivos: todosLosExpedientes.length,
    tiempoPromedioTotalDias: promedio(diasEnProceso),
    tiempoObjetivoTotalDias: tiemposObjetivo.length > 0 ? tiemposObjetivo.reduce((acc, v) => acc + v, 0) : null,
  };
}

export interface DistribucionEtapa {
  etapaId: string;
  etapaNombre: string;
  color: string | null;
  cantidad: number;
  tiempoPromedioDias: number | null;
}

export function calcularDistribucionPorEtapa(columnas: ColumnaKanban[]): DistribucionEtapa[] {
  return columnas.map((col) => ({
    etapaId: col.etapa.id,
    etapaNombre: col.etapa.nombre,
    color: col.etapa.color,
    cantidad: col.expedientes.length,
    tiempoPromedioDias: promedio(col.expedientes.map((e) => e.dias_en_etapa).filter((d): d is number => d != null)),
  }));
}

export interface CuelloDeBotella {
  etapaId: string;
  etapaNombre: string;
  tiempoPromedioDias: number;
  cantidad: number;
}

// Cuello de botella = la etapa con mayor tiempo promedio transcurrido
// entre las que tienen al menos un expediente asignado hoy. `null` si
// ninguna etapa tiene expedientes.
export function calcularCuelloDeBotella(columnas: ColumnaKanban[]): CuelloDeBotella | null {
  const candidatas = calcularDistribucionPorEtapa(columnas).filter(
    (d): d is DistribucionEtapa & { tiempoPromedioDias: number } => d.cantidad > 0 && d.tiempoPromedioDias != null,
  );
  if (candidatas.length === 0) return null;
  const peor = candidatas.reduce((acc, d) => (d.tiempoPromedioDias > acc.tiempoPromedioDias ? d : acc));
  return {
    etapaId: peor.etapaId,
    etapaNombre: peor.etapaNombre,
    tiempoPromedioDias: peor.tiempoPromedioDias,
    cantidad: peor.cantidad,
  };
}
